import {
  AddJsxChange,
  DuplicateChange,
} from "@/store/zustand/changeLedgerTypes";
import { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import {
  NavTreeUpdates,
  fixUniquePathsForAllChildren,
} from "./navTreeLookupUtils";
import { getCodebaseIdFromNode } from "@/components/Canvas/utils/utils";
import { getElementsForCodebaseId } from "./domUtils";
import { v4 as uuidv4 } from "uuid";
import {
  getNavTreeFromHtml,
  updateAllStoryboardIdsInNavTree,
} from "@/utils/navTreeUtils";
import _ from "lodash";
import { useTextEditStore } from "../hooks/textEditStore";
import { updateCodebaseId, ApplyInstantUpdateProps } from "./utils";

interface ApplyAddJsxProps extends ApplyInstantUpdateProps {
  replaceCodebaseId?: string;
}

export const applyAddJsx = (props: ApplyAddJsxProps) => {
  const { iframeDocument, storyboardId, replaceCodebaseId, persistentData } =
    props;
  const change = props.change as AddJsxChange;
  const changeFields = change.changeFields;
  const newAddedIds: string[] = [];

  if (changeFields.htmlForInstantUpdate) {
    const setCodebaseId = (element: HTMLElement): boolean => {
      // For elements pasted from tempo, the prev codebase id can be available
      const prevCodebaseId = getCodebaseIdFromNode(element);
      let newCodebaseId;
      if (prevCodebaseId) {
        newCodebaseId = changeFields.codebaseIdHashingKey
          ? updateCodebaseId(prevCodebaseId, changeFields.codebaseIdHashingKey)
          : prevCodebaseId;
      } else {
        newCodebaseId = `tempo-${uuidv4()}`;
      }

      element.setAttribute("data-tempoelementid", newCodebaseId);

      newAddedIds.push(newCodebaseId);

      let hadAnyPrevCodebaseId = Boolean(prevCodebaseId);
      Array.from(element.children).forEach((child) => {
        hadAnyPrevCodebaseId =
          hadAnyPrevCodebaseId || Boolean(setCodebaseId(child as HTMLElement));
      });

      return hadAnyPrevCodebaseId;
    };

    let savedElementToAdd: HTMLElement | undefined;
    const getElementToAdd = () => {
      if (savedElementToAdd)
        return savedElementToAdd.cloneNode(true) as HTMLElement;

      const html = changeFields.htmlForInstantUpdate;
      // Some html provided has the full structure including html, body, header, etc so that it displays correctly in a preview
      // iframe. We just want to strip this and take the contents of the body.
      const cleanedHtml = html!.includes("<body")
        ? html!.replace(/<body[^>]*>([\s\S]*)<\/body>/i, "$1")
        : html;

      // Create a temporary container to parse the HTML
      const tempDiv = iframeDocument.createElement("div");
      tempDiv.innerHTML = cleanedHtml!;

      // Get the first element to add
      const elementToAdd = tempDiv.firstElementChild as HTMLElement;
      if (!elementToAdd) return;

      const hadAnyPrevCodebaseId = setCodebaseId(elementToAdd);

      if (!hadAnyPrevCodebaseId) {
        persistentData.instantUpdateFailed = true;
        console.log("Instant update failed: No prev codebase id");
      }

      // Makr that this storyboard was changed
      persistentData.storyboardsAppliedTo.add(storyboardId);

      savedElementToAdd = elementToAdd;
      return elementToAdd;
    };

    // Check if this is a replacement operation
    if (replaceCodebaseId) {
      // Find the element to replace
      const elementsToReplace = getElementsForCodebaseId(
        iframeDocument,
        replaceCodebaseId,
      );

      elementsToReplace.forEach((elementToReplace) => {
        const clonedElement = getElementToAdd() as HTMLElement;
        if (!clonedElement) return;

        // Store the original element's HTML for undo
        if (!persistentData.instantUpdateData["replacedElements"]) {
          persistentData.instantUpdateData["replacedElements"] = [];
        }
        persistentData.instantUpdateData["replacedElements"].push({
          codebaseId: replaceCodebaseId,
          outerHTML: elementToReplace.outerHTML,
        });

        // Replace the element
        elementToReplace.replaceWith(clonedElement);
      });
    } else {
      // Normal add operation - find parent elements using getElementsForCodebaseId
      const parentElements = getElementsForCodebaseId(
        iframeDocument,
        changeFields.codebaseIdToAddTo || "",
      );

      parentElements.forEach((parentElement) => {
        const clonedElement = getElementToAdd() as HTMLElement;
        if (!clonedElement) return;

        if (changeFields.afterCodebaseId) {
          // Find the after element
          const afterElements = getElementsForCodebaseId(
            iframeDocument,
            changeFields.afterCodebaseId,
          );

          if (afterElements.length > 0) {
            const afterElement = afterElements[0];

            // Validate that the afterElement's parent is the current parentElement
            if (afterElement.parentElement === parentElement) {
              afterElement.insertAdjacentElement("afterend", clonedElement);
            } else {
              persistentData.instantUpdateFailed = true;
              console.log(
                "Instant update failed: after element not a direct child",
              );

              // If not a direct child, append to the end of parent
              parentElement.appendChild(clonedElement);
            }
          } else {
            persistentData.instantUpdateFailed = true;
            console.log("Instant update failed: after element not found");

            // If after element not found, append to the end
            parentElement.appendChild(clonedElement);
          }
        } else if (changeFields.beforeCodebaseId) {
          // Find the before element
          const beforeElements = getElementsForCodebaseId(
            iframeDocument,
            changeFields.beforeCodebaseId,
          );

          if (beforeElements.length > 0) {
            const beforeElement = beforeElements[0];

            // Validate that the beforeElement's parent is the current parentElement
            if (beforeElement.parentElement === parentElement) {
              beforeElement.insertAdjacentElement("beforebegin", clonedElement);
            } else {
              persistentData.instantUpdateFailed = true;
              console.log(
                "Instant update failed: before element not a direct child",
              );

              // If not a direct child, append to the end of parent
              parentElement.appendChild(clonedElement);
            }
          } else {
            persistentData.instantUpdateFailed = true;
            console.log("Instant update failed: before element not found");

            // If before element not found, append to the end
            parentElement.appendChild(clonedElement);
          }
        } else {
          // No specific position, append to the end
          parentElement.appendChild(clonedElement);
        }
      });
    }
  } else {
    persistentData.instantUpdateFailed = true;
    console.log("Instant update failed: No html for instant update");
  }

  // Important - only save if there were changes applies to this storyboard so that
  // irrelevant changes to other storyboards don't clear this state
  if (newAddedIds.length > 0) {
    persistentData.instantUpdateData["newAddedIds"] = newAddedIds;
  }
};

export const undoAddJsx = (props: ApplyAddJsxProps) => {
  const { iframeDocument, persistentData, replaceCodebaseId } = props;
  const change = props.change as AddJsxChange;
  // Check if this was a replacement operation
  if (
    replaceCodebaseId &&
    persistentData.instantUpdateData?.["replacedElements"]
  ) {
    // Restore the original replaced elements
    const replacedElements = persistentData.instantUpdateData[
      "replacedElements"
    ] as Array<{
      codebaseId: string;
      outerHTML: string;
    }>;

    // Get the saved added IDs
    const newAddedIds = persistentData.instantUpdateData?.["newAddedIds"] as
      | string[]
      | undefined;
    if (!newAddedIds || newAddedIds.length === 0) return;

    // Remove the new elements and restore the original ones
    newAddedIds.forEach((addedId: string) => {
      getElementsForCodebaseId(iframeDocument, addedId).forEach(
        (element, index) => {
          if (replacedElements[index]) {
            // Create a temporary container to parse the original HTML
            const tempDiv = iframeDocument.createElement("div");
            tempDiv.innerHTML = replacedElements[index].outerHTML;
            const originalElement = tempDiv.firstElementChild;
            if (originalElement) {
              element.replaceWith(originalElement);
            }
          }
        },
      );
    });
  } else {
    // Normal undo - just remove the added elements
    const newAddedIds = persistentData.instantUpdateData?.["newAddedIds"] as
      | string[]
      | undefined;
    if (!newAddedIds || newAddedIds.length === 0) return;

    // Remove each added element
    newAddedIds.forEach((addedId: string) => {
      getElementsForCodebaseId(iframeDocument, addedId).forEach((element) => {
        element.remove();
      });
    });
  }
};

// Update nav tree for ADD_JSX change
interface ApplyAddJsxNavTreeProps extends ApplyAddJsxProps {
  canvasId: string;
}

export const applyAddJsxToNavTree = (props: ApplyAddJsxNavTreeProps): void => {
  const {
    navTreeUpdates,
    storyboardId,
    canvasId,
    replaceCodebaseId,
    persistentData,
  } = props;
  const change = props.change as AddJsxChange;
  const { navTreeNodesForInstantUpdate } = change.changeFields;

  // If no compressed nav tree nodes provided, mark as failed
  if (!navTreeNodesForInstantUpdate?.length) {
    if (!change.changeFields.htmlForInstantUpdate) {
      persistentData.instantUpdateFailed = true;
      console.log("Instant update failed: No html for instant update");
      return;
    }

    const { newNavTree: navTreeFromHtml } = getNavTreeFromHtml(
      change.changeFields.htmlForInstantUpdate,
      storyboardId,
      null,
      canvasId,
      null,
      true,
    );

    if (navTreeFromHtml) {
      change.changeFields.navTreeNodesForInstantUpdate = [navTreeFromHtml];
    }
  }

  if (!change.changeFields.navTreeNodesForInstantUpdate?.length) {
    persistentData.instantUpdateFailed = true;
    console.log("Instant update failed: No nav tree nodes for instant update");
    return;
  }

  try {
    // Track all added nodes for undo
    const addedNodes: NavTreeNode[] = [];
    const parentsToFix = new Set<NavTreeNode>();

    // Process each compressed nav tree node
    change.changeFields.navTreeNodesForInstantUpdate.forEach(
      (navTreeNode, index) => {
        if (!navTreeNode) {
          persistentData.instantUpdateFailed = true;
          console.log("Instant update failed: No nav tree node");
          return;
        }

        const newNavTreeNode = _.cloneDeep(navTreeNode);

        // Find the parent node to add to
        const parentNodes =
          navTreeUpdates.codebaseIdLookup[
            change.changeFields.codebaseIdToAddTo || ""
          ] || [];
        if (parentNodes.length === 0) {
          // This may be the wrong storyboard, so no marking as failed
          return;
        }
        updateAllStoryboardIdsInNavTree(
          newNavTreeNode,
          storyboardId,
          change.changeFields.codebaseIdHashingKey,
        );

        // Handle replacement vs normal add
        if (replaceCodebaseId) {
          // For replacement, find the node to replace
          const nodesToReplace =
            navTreeUpdates.codebaseIdLookup[replaceCodebaseId] || [];

          nodesToReplace.forEach((nodeToReplace) => {
            // Store the original node for undo
            if (!persistentData.instantUpdateData["replacedNavTreeNodes"]) {
              persistentData.instantUpdateData["replacedNavTreeNodes"] = [];
            }
            persistentData.instantUpdateData["replacedNavTreeNodes"].push(
              _.cloneDeep(nodeToReplace),
            );

            const parent = nodeToReplace.parent;

            if (parent && parent.children) {
              // Node has a parent - replace in parent's children array
              const replaceIndex = parent.children.indexOf(nodeToReplace);
              if (replaceIndex !== -1) {
                // Set the parent reference for the new node
                newNavTreeNode.parent = parent;

                // Replace the node
                parent.children[replaceIndex] = newNavTreeNode;

                addedNodes.push(newNavTreeNode);
                parentsToFix.add(parent);
              }
            } else {
              // Node is a top-level node (no parent) - replace in the nav tree root
              // The new node becomes a top-level node
              newNavTreeNode.parent = undefined;

              // Replace in the main nav tree
              if (navTreeUpdates.navTree === nodeToReplace) {
                navTreeUpdates.navTree = newNavTreeNode;
              }

              addedNodes.push(newNavTreeNode);
              parentsToFix.add(newNavTreeNode);
            }
          });
        } else {
          // Normal add operation
          parentNodes.forEach((parentNode) => {
            // Update the decompressed node's parent reference
            newNavTreeNode.parent = parentNode;

            // Determine where to insert the node
            let insertionIndex = -1;

            if (change.changeFields.afterCodebaseId) {
              // Find the after node in parent's children
              const afterIndex =
                parentNode.children?.findIndex(
                  (child) =>
                    child.tempoElement?.codebaseId ===
                    change.changeFields.afterCodebaseId,
                ) ?? -1;

              if (afterIndex !== -1) {
                insertionIndex = afterIndex + 1;
              } else {
                persistentData.instantUpdateFailed = true;
                console.log(
                  "Instant update failed for nav tree: after node not found",
                );
              }
            } else if (change.changeFields.beforeCodebaseId) {
              // Find the before node in parent's children
              const beforeIndex =
                parentNode.children?.findIndex(
                  (child) =>
                    child.tempoElement?.codebaseId ===
                    change.changeFields.beforeCodebaseId,
                ) ?? -1;

              if (beforeIndex !== -1) {
                insertionIndex = beforeIndex;
              } else {
                persistentData.instantUpdateFailed = true;
                console.log(
                  "Instant update failed for nav tree: before node not found",
                );
              }
            }

            // Insert the node at the appropriate position
            if (!parentNode.children) {
              parentNode.children = [];
            }

            if (
              insertionIndex >= 0 &&
              insertionIndex <= parentNode.children.length
            ) {
              parentNode.children.splice(insertionIndex, 0, newNavTreeNode);
            } else {
              // Append to the end if no specific position or invalid index
              parentNode.children.push(newNavTreeNode);
            }

            addedNodes.push(newNavTreeNode);
            parentsToFix.add(parentNode);
          });
        }
      },
    );

    // Fix unique paths for all affected parents
    parentsToFix.forEach((parent) => fixUniquePathsForAllChildren(parent));

    if (addedNodes.length === 1 && change.changeFields.editTextAfterAdd) {
      const nodeToEdit = addedNodes[0];
      useTextEditStore
        .getState()
        .requestTextEdit(
          storyboardId,
          nodeToEdit.domUniquePath ?? nodeToEdit.domUniquePathForComponent,
        );

      // Clear so this only happens once
      change.changeFields.editTextAfterAdd = false;
    }
  } catch (error) {
    console.error(
      "Instant update failed for nav tree: Failed to apply error: ",
      error,
    );
    persistentData.instantUpdateFailed = true;
  }
};

// Undo nav tree changes for ADD_JSX change
export const undoAddJsxToNavTree = (props: ApplyAddJsxProps): void => {
  const { navTreeUpdates, replaceCodebaseId, persistentData } = props;
  const change = props.change as AddJsxChange;
  // Get the saved added IDs from the DOM operation
  const newAddedIds = persistentData.instantUpdateData?.["newAddedIds"] as
    | string[]
    | undefined;
  if (!newAddedIds || newAddedIds.length === 0) return;

  const parentsToFix = new Set<NavTreeNode>();

  // Check if this was a replacement operation
  if (
    replaceCodebaseId &&
    persistentData.instantUpdateData?.["replacedNavTreeNodes"]
  ) {
    // Restore the original replaced nodes
    const replacedNavTreeNodes = persistentData.instantUpdateData[
      "replacedNavTreeNodes"
    ] as NavTreeNode[];

    // Remove the new nodes and restore the original ones
    newAddedIds.forEach((addedCodebaseId: string, index) => {
      const nodesToRemove =
        navTreeUpdates.codebaseIdLookup[addedCodebaseId] || [];

      nodesToRemove.forEach((nodeToRemove) => {
        const originalNode = replacedNavTreeNodes[index];
        if (!originalNode) return;

        const parent = nodeToRemove.parent;

        if (parent && parent.children) {
          // Node has a parent - replace in parent's children array
          const removeIndex = parent.children.indexOf(nodeToRemove);
          if (removeIndex !== -1) {
            // Restore the original node
            originalNode.parent = parent;
            parent.children[removeIndex] = originalNode;
            parentsToFix.add(parent);
          }
        } else {
          // Node is a top-level node (no parent) - restore in the nav tree root
          originalNode.parent = undefined;

          // Restore in the main nav tree
          if (navTreeUpdates.navTree === nodeToRemove) {
            navTreeUpdates.navTree = originalNode;
          }

          parentsToFix.add(originalNode);
        }
      });
    });
  } else {
    // Normal undo - just remove the added nodes
    newAddedIds.forEach((addedCodebaseId: string) => {
      const nodesToRemove =
        navTreeUpdates.codebaseIdLookup[addedCodebaseId] || [];

      nodesToRemove.forEach((nodeToRemove) => {
        const parent = nodeToRemove.parent;
        if (!parent) return;

        // Remove the node from parent's children
        const index = parent.children?.indexOf(nodeToRemove) ?? -1;
        if (index !== -1) {
          parent.children.splice(index, 1);
          parentsToFix.add(parent);
        }
      });
    });
  }

  // Fix unique paths for all affected parents
  parentsToFix.forEach((parent) => fixUniquePathsForAllChildren(parent));
};

// Consolidated apply function that handles both DOM and nav tree
export const applyAddJsxChange = (props: ApplyAddJsxNavTreeProps): void => {
  // Apply DOM changes
  applyAddJsx(props);

  // Apply nav tree changes
  if (!props.skipUpdateNavTree) {
    applyAddJsxToNavTree(props);
  }
};

// Consolidated undo function that handles both DOM and nav tree
export const undoAddJsxChange = (props: ApplyAddJsxProps): void => {
  // Undo DOM changes
  undoAddJsx(props);

  // Undo nav tree changes
  if (!props.skipUpdateNavTree) {
    undoAddJsxToNavTree(props);
  }
};
